/**
 * ANA-R3-TILE-03 — Tiles display geometry: size presets + the
 * zoom-to-fit-then-cap crop algorithm.
 *
 * Pure, framework-free helpers so the crop math is unit-testable without a
 * canvas/DOM. `Tile.tsx` consumes both; `TilesWidget.tsx` consumes
 * `TILE_SIZE_PX` for the grid `minmax()` floor + `gridAutoRows`.
 */
import type { TileSize } from "../../preferences/index.js";

/**
 * Tile-size presets → on-screen cell side in CSS px (Part B). Exact values
 * are not load-bearing (OQ2: "perfect"); `small` keeps the historical ~270 px
 * perceived size, scaling up to medium/large for on-tile editing (GEOM-05).
 * Drives the grid `minmax()` column floor AND `gridAutoRows` in TilesWidget,
 * and the canvas display size in Tile.
 */
export const TILE_SIZE_PX: Record<TileSize, number> = {
  // ANA-R4-TILE-01 — rescaled down (Christian 2026-06-11): small −25%,
  // medium/large −⅓. Was 270 / 400 / 520. Verify perceived sizes at 100% zoom.
  small: 200,
  medium: 270,
  large: 345,
};

/** Padding around the defect shape, in SOURCE px (story Part A step 2; OQ1). */
export const TILE_CROP_PADDING_PX = 20;

export interface TileCropInput {
  /** Bbox in normalised [0,1] coords (x, y, w, h) — the tile crop input. */
  bbox: { x: number; y: number; w: number; h: number };
  /** Source image width in px. */
  imgW: number;
  /** Source image height in px. */
  imgH: number;
  /** Tile display side in px (the chosen preset, device-pixel-agnostic). */
  tileDisplayPx: number;
  /** Max-zoom cap as a PERCENT of native resolution (e.g. 1000 = 10×). */
  maxZoomPercent: number;
}

export interface TileCropResult {
  /** Square source-crop side in px (clamped to image bounds). */
  cropSide: number;
  /** Top-left source x of the crop (clamped inside the image). */
  sx: number;
  /** Top-left source y of the crop (clamped inside the image). */
  sy: number;
  /** Effective magnification actually used (displayed px / source px). */
  zoom: number;
  /** True iff the fit-zoom was clamped down to the cap (small-defect path). */
  capped: boolean;
}

/**
 * Zoom-to-fit-then-cap (story Part A + updated AC3).
 *
 *   1. Compute the shape's source extent (sw, sh) from the normalised bbox.
 *   2. Pad ~20 source px around it; square it → `regionSide`.
 *   3. Fit zoom `Z = tileDisplayPx / regionSide`.
 *   4. Clamp `Z <= maxZoomPercent/100` (the cap) — tiny defects stop at the
 *      cap (appear smaller, more context) rather than hyper-zooming.
 *      `cropSide = tileDisplayPx / Z`.
 *   5. Clamp `cropSide` to image bounds; centre on the shape centroid; clamp
 *      the origin inside the image.
 *
 * Note there is no lower zoom bound here beyond the cap: a defect whose
 * padded region is LARGER than the tile yields `Z < 1` (zoom out) so the
 * full padded shape still frames with its ~20 px margin (OQ3).
 */
export function computeTileCrop(input: TileCropInput): TileCropResult {
  const { bbox, imgW, imgH, tileDisplayPx, maxZoomPercent } = input;

  const sw = bbox.w * imgW;
  const sh = bbox.h * imgH;

  // Padded, squared ideal region around the shape (step 2).
  const regionSide = Math.max(sw, sh) + 2 * TILE_CROP_PADDING_PX;

  // Fit zoom (step 3) then clamp to the cap (step 4).
  const zIdeal = tileDisplayPx / regionSide;
  const zCap = maxZoomPercent / 100;
  const zoom = Math.min(zIdeal, zCap);
  const capped = zIdeal > zCap;

  // Source crop side implied by the (possibly capped) zoom (step 4).
  let cropSide = tileDisplayPx / zoom;

  // Clamp to image bounds (step 5). A tiny image smaller than the crop just
  // shows the whole image.
  cropSide = Math.min(cropSide, imgW, imgH);

  // Centre on the shape centroid; clamp the origin inside the image (step 5).
  const cx = (bbox.x + bbox.w / 2) * imgW;
  const cy = (bbox.y + bbox.h / 2) * imgH;
  let sx = Math.round(cx - cropSide / 2);
  let sy = Math.round(cy - cropSide / 2);
  sx = Math.max(0, Math.min(Math.round(imgW - cropSide), sx));
  sy = Math.max(0, Math.min(Math.round(imgH - cropSide), sy));

  return { cropSide, sx, sy, zoom, capped };
}
